import {useEffect} from 'react';

import {assert} from 'Asserts';
import {
  MethodAnalysisSources,
  WorksheetAnalysisSources,
} from 'procedures/design/ProcedureMethodAnalysisTypes';
import {
  fetchProcedureMethodDefinitionAnalysisSources,
  fetchWorksheetAnalysisSources,
} from 'procedures/network/ProcedureMethodAnalysisDAO';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';

export function useAnalysisSourcesDuringConfig(methodId: string | null): MethodAnalysisSources | null {
  const [analysisSources, setAnalysisSources] = useStateIfMounted<MethodAnalysisSources | null>(null);
  useEffect(() => {
    async function fetchAnalysisSources(): Promise<void> {
      assert(methodId != null, 'Expected method id to exist for analysis template source table');

      const result = await fetchProcedureMethodDefinitionAnalysisSources(methodId);
      setAnalysisSources(result);
    }
    fetchAnalysisSources();
    // Only re-fetch if the method id changes, not setAnalysisSources
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [methodId]);

  return analysisSources;
}

export function useAnalysisSourcesDuringExecution(
  worksheetId: string,
  catchErrors: boolean = false
): {
  analysisSources: WorksheetAnalysisSources | null;
  error: Error | null;
  isLoading: boolean;
} {
  const [analysisSources, setAnalysisSources] = useStateIfMounted<WorksheetAnalysisSources | null>(null);
  const [error, setError] = useStateIfMounted<Error | null>(null);
  const [isLoading, setIsLoading] = useStateIfMounted<boolean>(true);

  useEffect(() => {
    async function fetchAnalysisSources(): Promise<void> {
      setAnalysisSources(null);
      setError(null);
      setIsLoading(true);

      try {
        const result = await fetchWorksheetAnalysisSources(worksheetId);
        setAnalysisSources(result);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch analysis sources'));
        if (!catchErrors) {
          throw err;
        }
      } finally {
        setIsLoading(false);
      }
    }

    fetchAnalysisSources();
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [worksheetId, catchErrors]);

  return {analysisSources, error, isLoading};
}
